import React, { useState, useEffect, useRef } from 'react';
import { FaShoppingCart, FaChevronDown, FaTimes, FaPlus, FaBox, FaCircle, FaEye, FaEyeSlash, FaArrowDown, FaArrowLeft, FaArrowRight, FaExpand, FaAlignLeft, FaAlignRight } from 'react-icons/fa';
import './CartSettings.css';
import { DEVICES } from '../../constants/breakpoints';

const CART_POSITIONS = {
  UNDER_BUTTON_LEFT: 'under-button-left',
  UNDER_BUTTON_RIGHT: 'under-button-right',
  LEFT_SIDE: 'left-side',
  RIGHT_SIDE: 'right-side',
  CENTER: 'center'
};

const POSITION_STYLES = {
  [CART_POSITIONS.UNDER_BUTTON_LEFT]: {
    container: {
      position: 'relative',
      justifyContent: 'start',
      backgroundColor: 'transparent'
    },
    wrapper: {
      position: 'absolute',
      height: 'fit-content',
      transform: 'translateY(0)',
      top: 'auto'
    }
  },
  [CART_POSITIONS.UNDER_BUTTON_RIGHT]: {
    container: {
      position: 'relative',
      justifyContent: 'end',
      backgroundColor: 'transparent'
    },
    wrapper: {
      position: 'absolute',
      height: 'fit-content',
      transform: 'translateY(0)',
      top: 'auto'
    }
  },
  [CART_POSITIONS.LEFT_SIDE]: {
    container: {
      position: 'absolute',
      inset: '0px',
      backgroundColor: '#0e0e0e4a',
      display: 'flex',
      justifyContent: 'start'
    },
    wrapper: {
      position: 'fixed',
      top: 'auto',
      height: '100%',
      transform: 'translateY(0)'
    }
  },
  [CART_POSITIONS.RIGHT_SIDE]: {
    container: {
      position: 'absolute',
      inset: '0px',
      backgroundColor: '#0e0e0e4a',
      display: 'flex',
      justifyContent: 'end'
    },
    wrapper: {
      position: 'fixed',
      height: '100%',
      top: 'auto',
      transform: 'translateY(0)'
    }
  },
  [CART_POSITIONS.CENTER]: {
    container: {
      position: 'absolute',
      inset: '0px',
      backgroundColor: '#0e0e0e4a',
      display: 'flex',
      justifyContent: 'center'
    },
    wrapper: {
      position: 'fixed',
      height: 'fit-content',
      top: '50%',
      transform: 'translateY(-50%)'
    }
  }
};

const CartSettings = ({ 
  element, 
  elements, 
  setElements, 
  updateElementText, 
  classStylesRegistry, 
  setClassStylesRegistry, 
  selectedDevice,
  pages,
  setPages
}) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showTestProduct, setShowTestProduct] = useState(false);
  
  // Get cart position from home page
  const [selectedPosition, setSelectedPosition] = useState(() => {
    const homePage = pages.find(page => page.isHome);
    return homePage?.cartPosition || CART_POSITIONS.UNDER_BUTTON_LEFT;
  });
  const prevOpenState = useRef(isCartOpen);


  // Cart product class styles
  const cartProductClasses = {
    'cart-product': {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 0px',
      borderBottom: '1px solid #333333',
      gap: '15px',
      width: '100%'
    },
    'cart-product-image': {
      width: '80px',
      height: '80px',
      objectFit: 'cover',
      borderRadius: '4px',
      backgroundColor: '#2a2a2a'
    },
    'cart-product-info': {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    'cart-product-name': {
      margin: '0',
      fontSize: '16px',
      fontWeight: '500',
      color: '#ffffff'
    },
    'cart-product-quantity': {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: '#999999',
      fontSize: '14px'
    },
    'cart-product-price': {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      minWidth: '80px',
      textAlign: 'right'
    },
    'cart-product-remove': {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#ff4444',
      cursor: 'pointer',
      padding: '5px',
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '30px',
      height: '30px'
    },
    'quantity-btn': {
      width: '28px',
      height: '28px',
      backgroundColor: '#333333',
      border: 'none',
      borderRadius: '4px',
      color: '#ffffff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease'
    },
    'quantity-btn-minus': {
      width: '28px',
      height: '28px',
      backgroundColor: '#333333',
      border: 'none',
      borderRadius: '4px',
      color: '#ffffff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease'
    },
    'quantity-btn-plus': {
      width: '28px',
      height: '28px',
      backgroundColor: '#333333',
      border: 'none',
      borderRadius: '4px',
      color: '#ffffff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease'
    },
    'quantity-value': {
      display: 'inline-block',
      minWidth: '20px',
      textAlign: 'center',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  // Update how we find cart elements
  const cartContainer = elements.find(el => 
    el.parentId === element.id && 
    el.attributes?.['data-element'] === 'cart-container'
  );

  const cartWrapper = elements.find(el => 
    el.parentId === cartContainer?.id && 
    el.type === 'cart-wrapper'
  );

  // Find test product element
  const findTestProduct = () => {
    if (!cartWrapper) return null;
    
    // Find cart content
    const cartContent = elements.find(el => 
      el.parentId === cartWrapper.id && el.type === 'cart-content'
    );
    
    if (!cartContent) return null;
    
    // Find test product
    return elements.find(el => 
      el.parentId === cartContent.id && 
      el.attributes && 
      el.attributes['data-element'] === 'cart-item' &&
      el.attributes['data-product-id'] === 'Test-Product-1'
    );
  };

  // Find empty state element
  const findEmptyState = () => {
    if (!cartWrapper) return null;
    
    // Find cart content
    const cartContent = elements.find(el => 
      el.parentId === cartWrapper.id && el.type === 'cart-content'
    );
    
    if (!cartContent) return null;
    
    // Find empty state
    return elements.find(el => 
      el.parentId === cartContent.id && 
      el.attributes && 
      el.attributes['data-element'] === 'empty-state'
    );
  };

  // Toggle test product visibility
  const toggleTestProduct = () => {
    const testProduct = findTestProduct();
    if (!testProduct) return;
    
    // Toggle visibility state
    const newShowState = !showTestProduct;
    setShowTestProduct(newShowState);
    
    // Update test product display style
    const updatedTestProduct = {
      ...testProduct,
      styles: {
        ...testProduct.styles,
        display: newShowState ? 'flex' : 'none'
      }
    };
    
    // Find empty state container
    const emptyState = findEmptyState();
    
    // Find cart total text
    const cartTotal = elements.find(el => 
      el.parentId === cartWrapper.id && 
      el.attributes && 
      el.attributes['data-element'] === 'cart-total'
    );
    
    const cartTotalText = cartTotal ? elements.find(el => 
      el.parentId === cartTotal.id && 
      el.attributes && 
      el.attributes['data-element'] === 'cart-total-text'
    ) : null;
    
    // Find checkout button
    const checkoutButton = elements.find(el => 
      el.parentId === cartWrapper.id && 
      el.attributes && 
      el.attributes['data-element'] === 'checkout-button'
    );
    
    // Find cart button
    const cartButton = elements.find(el => 
      el.parentId === element.id && el.type === 'cart-button'
    );
    
    // Update elements
    setElements(prevElements => 
      prevElements.map(el => {
        if (el.id === testProduct.id) {
          return updatedTestProduct;
        }
        
        // Update empty state container - this will affect all its children automatically
        if (emptyState && el.id === emptyState.id) {
          return {
            ...el,
            styles: {
              ...el.styles,
              display: newShowState ? 'none' : 'flex'
            }
          };
        }
        
        // Update cart total text
        if (cartTotalText && el.id === cartTotalText.id) {
          return {
            ...el,
            text: newShowState ? 'Total: $49.99' : 'Total: $0.00',
            attributes: {
              ...el.attributes,
              'data-amount': newShowState ? '49.99' : '0.00'
            }
          };
        }
        
        // Update checkout button
        if (checkoutButton && el.id === checkoutButton.id) {
          return {
            ...el,
            attributes: {
              ...el.attributes,
              disabled: newShowState ? false : true
            }
          };
        }
        
        // Update cart button
        if (cartButton && el.id === cartButton.id) {
          return {
            ...el,
            text: newShowState ? 'Cart (1)' : 'Cart (0)',
            attributes: {
              ...el.attributes,
              'data-count': newShowState ? '1' : '0'
            }
          };
        }
        
        return el;
      })
    );
  };

  // Update handlePositionChange to save position in pages state
  const handlePositionChange = (position) => {
    console.log('Current position:', position); // Debug log
    
    if (!POSITION_STYLES[position]) {
      console.error('Invalid position:', position);
      position = CART_POSITIONS.UNDER_BUTTON_LEFT; // Fallback to default
    }
    
    setSelectedPosition(position);
    
    if (!cartContainer || !cartWrapper) return;

    const containerStyles = {
      ...cartContainer.styles,
      ...POSITION_STYLES[position].container
    };

    const wrapperStyles = {
      ...cartWrapper.styles,
      ...POSITION_STYLES[position].wrapper
    };

    // Update pages state to store cart position
    setPages(prevPages => 
      prevPages.map(page => 
        page.isHome ? { ...page, cartPosition: position } : page
      )
    );

    // Update element styles
    setElements(prevElements => 
      prevElements.map(el => {
        if (el.id === cartWrapper.id) {
          return { ...el, styles: wrapperStyles };
        }
        if (el.id === cartContainer.id) {
          return { ...el, styles: containerStyles };
        }
        return el;
      })
    );
  };

  // Add useEffect to update position when pages change
  useEffect(() => {
    const homePage = pages.find(page => page.isHome);
    if (homePage?.cartPosition && homePage.cartPosition !== selectedPosition) {
      setSelectedPosition(homePage.cartPosition);
    }
  }, [pages]);

  // Add this useEffect after other useEffects
  useEffect(() => {
    if (cartContainer && cartWrapper) {
      // Add safety check for position styles
      const positionStyles = POSITION_STYLES[selectedPosition] || POSITION_STYLES[CART_POSITIONS.UNDER_BUTTON_LEFT];
      
      const containerStyles = {
        ...cartContainer.styles,
        ...positionStyles.container
      };

      const wrapperStyles = {
        ...cartWrapper.styles,
        ...positionStyles.wrapper
      };

      setElements(prevElements => 
        prevElements.map(el => {
          if (el.id === cartWrapper.id) {
            return { ...el, styles: wrapperStyles };
          }
          if (el.id === cartContainer.id) {
            return { ...el, styles: containerStyles };
          }
          return el;
        })
      );
    }
  }, [selectedPosition, cartContainer?.id, cartWrapper?.id]); // Run when position changes or cart elements are found

  // Update the cart open/close effect to respect the position styles
  useEffect(() => {
    if (cartContainer && cartWrapper && prevOpenState.current !== isCartOpen) {
      prevOpenState.current = isCartOpen;
      
      // Add safety check for position styles
      const positionStyles = POSITION_STYLES[selectedPosition] || POSITION_STYLES[CART_POSITIONS.UNDER_BUTTON_LEFT];
      
      setElements(prevElements => 
        prevElements.map(el => {
          if (el.id === cartContainer.id) {
            return {
              ...el,
              styles: {
                ...el.styles,
                ...positionStyles.container,
                display: isCartOpen ? 'flex' : 'none'
              }
            };
          }
          if (el.id === cartWrapper.id) {
            return {
              ...el,
              styles: {
                ...el.styles,
                ...positionStyles.wrapper,
                display: isCartOpen ? 'flex' : 'none'
              }
            };
          }
          return el;
        })
      );
    }
  }, [isCartOpen, cartContainer?.id, cartWrapper?.id, selectedPosition]);

  // Check if test product exists and update state accordingly
  useEffect(() => {
    const testProduct = findTestProduct();
    if (testProduct) {
      setShowTestProduct(testProduct.styles.display !== 'none');
    }
  }, [elements]);

  const handleCartToggle = () => {
    setIsCartOpen(!isCartOpen);
  };

  return (
    <div className="cart-settings">
      <div className="cart-settings-header">
        <FaShoppingCart className="header-icon" />
        <h4 className="cart-settings-title">Cart Settings</h4>
      </div>
      
      <div className="cart-settings-content">
        <div className="cart-position-section">
          <h5 className="section-title">Cart Position</h5>
          <div className="position-options">
            <button 
              className={`position-option ${selectedPosition === CART_POSITIONS.UNDER_BUTTON_LEFT ? 'active' : ''}`}
              onClick={() => handlePositionChange(CART_POSITIONS.UNDER_BUTTON_LEFT)}
            >
              <div className="position-option-icon">
                <FaAlignLeft />
              </div>
              <span className="position-option-text">Under Left</span>
            </button>
            <button 
              className={`position-option ${selectedPosition === CART_POSITIONS.UNDER_BUTTON_RIGHT ? 'active' : ''}`}
              onClick={() => handlePositionChange(CART_POSITIONS.UNDER_BUTTON_RIGHT)}
            >
              <div className="position-option-icon">
                <FaAlignRight />
              </div>
              <span className="position-option-text">Under Right</span>
            </button>
            <button 
              className={`position-option ${selectedPosition === CART_POSITIONS.LEFT_SIDE ? 'active' : ''}`}
              onClick={() => handlePositionChange(CART_POSITIONS.LEFT_SIDE)}
            >
              <div className="position-option-icon">
                <FaArrowLeft />
              </div>
              <span className="position-option-text">Left Side</span>
            </button>
            <button 
              className={`position-option ${selectedPosition === CART_POSITIONS.RIGHT_SIDE ? 'active' : ''}`}
              onClick={() => handlePositionChange(CART_POSITIONS.RIGHT_SIDE)}
            >
              <div className="position-option-icon">
                <FaArrowRight />
              </div>
              <span className="position-option-text">Right Side</span>
            </button>
            <button 
              className={`position-option ${selectedPosition === CART_POSITIONS.CENTER ? 'active' : ''}`}
              onClick={() => handlePositionChange(CART_POSITIONS.CENTER)}
              style={{ gridColumn: 'span 2' }}
            >
              <div className="position-option-icon">
                <FaExpand />
              </div>
              <span className="position-option-text">Center</span>
            </button>
          </div>
        </div>

        <div className="cart-toggle-section">
          <div className="toggle-header">
            <label className="cart-toggle-label">Cart State</label>
            <span className="toggle-status">
              <FaCircle 
                size={8} 
                style={{ 
                  marginRight: '4px',
                  color: isCartOpen ? '#4CAF50' : '#dc3545'
                }} 
              />
              {isCartOpen ? 'Open' : 'Closed'}
            </span>
          </div>
          
          <button 
            className={`cart-toggle-button ${isCartOpen ? 'open' : ''}`}
            onClick={handleCartToggle}
          >
            {isCartOpen ? (
              <>
                <FaTimes className="button-icon" />
                <span>Close Cart</span>
              </>
            ) : (
              <>
                <FaShoppingCart className="button-icon" />
                <span>Open Cart</span>
              </>
            )}
          </button>
        </div>

        <div className="cart-test-products-section">
          <div className="section-header-test-products">
            <h5>
              <FaBox style={{ marginRight: '8px' }} />
              Test Products
            </h5>
            <button 
              className={`toggle-test-products-button ${showTestProduct ? 'active' : ''}`}
              onClick={toggleTestProduct}
            >
              {showTestProduct ? (
                <>
                  <FaEyeSlash className="button-icon" />
                  <span>Hide Test Product</span>
                </>
              ) : (
                <>
                  <FaEye className="button-icon" />
                  <span>Show Test Product</span>
                </>
              )}
            </button>
          </div>
          <p className="section-description">
            {showTestProduct 
              ? 'Test product is visible in the cart. Hide it to see the empty cart state.' 
              : 'Test product is hidden. Show it to preview how products look in the cart.'}
          </p>
        </div>

        <div className="cart-info-section">
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className={`info-value ${isCartOpen ? 'status-active' : 'status-inactive'}`}>
              {isCartOpen ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Test Product</span>
            <span className={`info-value ${showTestProduct ? 'status-active' : 'status-inactive'}`}>
              {showTestProduct ? 'Visible' : 'Hidden'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartSettings; 